/**
 * Screenplay element-type detection.
 * Pure heuristics + Fountain conventions — no external services.
 *
 * Element types:
 *   scene_heading, action, character, dialogue, parenthetical,
 *   transition, shot, subheader, lyric, note, section
 */

const SCENE_PREFIX_RE = /^(INT|EXT|EST|INT\.?\/EXT|I\/E|INT\/EXT)[.\s]/i;
const TRANSITION_RE = /\bTO:\s*$/i;
const FADE_RE = /^FADE\s+(IN|OUT)/i;
const CUT_RE = /^(CUT|SMASH\s+CUT|MATCH\s+CUT|DISSOLVE|JUMP\s+CUT)\b/i;
const SHOT_RE = /^(CLOSE\s+ON|ANGLE\s+ON|POV|REVERSE\s+ANGLE|INSERT|WIDE|TWO\s+SHOT|OTS|TIGHT\s+ON|EXTREME\s+CLOSE\s+UP|ECU)\b/i;
const PAREN_RE = /^\(.+\)\s*$/;
const FORCE_SCENE_RE = /^\./;       // Fountain: starts with "." forces scene
const FORCE_CHARACTER_RE = /^@/;    // Fountain: starts with "@" forces character
const FORCE_TRANSITION_RE = /^>\s*/;
const SECTION_RE = /^#+\s/;
const SUBHEADER_RE = /^##?\s/;
const LYRIC_RE = /^~/;
const NOTE_RE = /^\[\[.+\]\]$/;

const ALL_CAPS_RE = /^[^a-z]*$/;
const HAS_LETTER_RE = /[A-Za-z]/;

const EXTENSION_RE = /(?:\s*\([^)]*\))+\s*$/;

export const ELEMENT_LABELS = {
  scene_heading: "Scene",
  action: "Action",
  character: "Character",
  dialogue: "Dialogue",
  parenthetical: "Parenthetical",
  transition: "Transition",
  shot: "Shot",
  subheader: "Subheader",
  section: "Section",
  lyric: "Lyric",
  note: "Note",
};

export const ELEMENT_ORDER = [
  "action",
  "scene_heading",
  "character",
  "dialogue",
  "parenthetical",
  "transition",
  "shot",
  "subheader",
  "section",
  "lyric",
  "note",
];

/**
 * Detect the element type for a given line based on its content and the
 * previous line's type. Returns one of ELEMENT_LABELS keys.
 *
 * @param {string} rawText
 * @param {string|null} prevType
 * @param {string|null} prevText
 */
export function detectElementType(rawText, prevType = null, prevText = "") {
  const text = (rawText || "").trim();
  if (!text) return "action";

  // Forced types (Fountain conventions)
  if (FORCE_SCENE_RE.test(text)) return "scene_heading";
  if (FORCE_CHARACTER_RE.test(text)) return "character";
  if (FORCE_TRANSITION_RE.test(text)) return "transition";
  if (SUBHEADER_RE.test(text)) return "subheader";
  if (SECTION_RE.test(text)) return "section";
  if (LYRIC_RE.test(text)) return "lyric";
  if (NOTE_RE.test(text)) return "note";

  // Scene heading
  if (SCENE_PREFIX_RE.test(text)) return "scene_heading";

  // Transitions
  if (FADE_RE.test(text)) return "transition";
  if (CUT_RE.test(text) && ALL_CAPS_RE.test(text)) return "transition";
  if (TRANSITION_RE.test(text) && ALL_CAPS_RE.test(text)) return "transition";

  // Shots
  if (SHOT_RE.test(text)) return "shot";

  // Parenthetical — only inside dialogue blocks
  if (PAREN_RE.test(text) && (prevType === "character" || prevType === "dialogue" || prevType === "parenthetical")) {
    return "parenthetical";
  }

  // Character — all-caps short line, not after another character
  if (
    HAS_LETTER_RE.test(text) &&
    ALL_CAPS_RE.test(text.replace(EXTENSION_RE, "")) &&
    text.length <= 38 &&
    !text.endsWith(".") &&
    prevType !== "character" &&
    prevType !== "parenthetical"
  ) {
    return "character";
  }

  // Dialogue — line after character or parenthetical
  if (prevType === "character" || prevType === "parenthetical") {
    return "dialogue";
  }
  if (prevType === "dialogue" && !text.endsWith(".") && text.length < 80 && ALL_CAPS_RE.test(text) && HAS_LETTER_RE.test(text)) {
    // edge case ignored — keep as dialogue continuation
    return "dialogue";
  }

  return "action";
}

/** What element type usually follows a given type when the user hits Enter. */
export function nextDefaultType(currentType) {
  switch (currentType) {
    case "scene_heading":
      return "action";
    case "character":
      return "dialogue";
    case "parenthetical":
      return "dialogue";
    case "dialogue":
      return "action";
    case "shot":
      return "action";
    case "transition":
      return "scene_heading";
    case "subheader":
      return "action";
    default:
      return "action";
  }
}

/** Pulls (V.O.) etc. off a character line, returning {name, extension}.
 *  Strips ALL trailing parenthetical extensions ("MATT (V.O.) (FILTERED)" → MATT);
 *  if stripping would leave nothing, the original text is kept as the name. */
export function splitCharacterExtension(text) {
  const m = text.match(EXTENSION_RE);
  if (!m) return { name: text.trim(), extension: null };
  const name = text.replace(EXTENSION_RE, "").trim();
  if (!name) return { name: text.trim(), extension: null };
  return { name, extension: m[0].trim() };
}

/** Cycle to the next type when user presses Tab. */
export function cycleType(current) {
  const visible = ["action", "scene_heading", "character", "parenthetical", "dialogue", "transition", "shot"];
  const idx = visible.indexOf(current);
  return visible[(idx + 1) % visible.length];
}

/** Compute scene numbers (1-based) for an elements array. */
export function computeSceneNumbers(elements) {
  let n = 0;
  return elements.map((el) => {
    if (el.type === "scene_heading") {
      n += 1;
      return { ...el, _sceneNumber: n };
    }
    return el;
  });
}

/** Build an outline from elements (scene headings only). */
export function buildOutline(elements) {
  const out = [];
  let n = 0;
  elements.forEach((el, i) => {
    if (el.type === "scene_heading") {
      n += 1;
      out.push({ index: i, number: n, text: el.text, id: el.id });
    }
  });
  return out;
}

function _wordCount(t) {
  return ((t || "").trim().match(/\S+/g) || []).length;
}

function _escapeRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Whole-word matcher for a character name in action prose. Matches the name as a
// proper noun (ALL CAPS or Title Case) but NOT as a lowercase common word — so a
// character named "WILL" is credited for "Will runs." but not "he will run".
function _nameMatcher(key) {
  const titled = key.split(/\s+/).map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w)).join(" ");
  const forms = Array.from(new Set([key, titled])).map(_escapeRe).join("|");
  return new RegExp(`(^|[^A-Za-z0-9])(${forms})([^A-Za-z0-9]|$)`);
}

/**
 * Analyze how each character is used across the script.
 *
 * Returns:
 *   names  — Set of UPPERCASED character-cue names present in the script
 *   stats  — { [UPPERNAME]: { firstIndex, firstScene, dialogueWords, actionWords,
 *               screenTime (== dialogueWords, kept for back-compat), occurrences:[
 *               { index, elementId, sceneNumber, sceneHeading, lineText } ] } }
 *
 * dialogueWords = words in the dialogue/parenthetical block(s) after each cue.
 * actionWords   = words of action attributed to the character via a per-scene
 *   "current subject" carry: action lines naming a character credit that
 *   character (and make them the current subject); unnamed action lines credit
 *   the current subject(s) (pronoun/implied action). Scenery before anyone is
 *   established stays uncredited. Both are converted to time at ~190 words/minute.
 */
export function analyzeScriptCharacters(elements) {
  const els = elements || [];
  const stats = {};
  const names = new Set();

  const ensure = (key) => {
    if (!stats[key]) stats[key] = { firstIndex: Infinity, firstScene: 0, dialogueWords: 0, actionWords: 0, screenTime: 0, occurrences: [] };
    return stats[key];
  };

  // Pass 1 — gather every cue name so action anywhere can be matched to them.
  for (const el of els) {
    if (el.type === "character") {
      const { name } = splitCharacterExtension((el.text || "").trim());
      const key = (name || "").toUpperCase().trim();
      if (key) names.add(key);
    }
  }
  const matchers = Array.from(names)
    .sort((a, b) => b.length - a.length)
    .map((key) => ({ key, re: _nameMatcher(key) }));

  // Pass 2 — walk the script attributing dialogue and action time.
  let sceneNumber = 0;
  let sceneHeading = "";
  let currentSubjects = new Set();

  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    const type = el.type;

    if (type === "scene_heading") {
      sceneNumber += 1;
      sceneHeading = el.text || "";
      currentSubjects = new Set();
      continue;
    }

    if (type === "character") {
      const { name } = splitCharacterExtension((el.text || "").trim());
      const key = (name || "").toUpperCase().trim();
      if (!key) continue;
      const s = ensure(key);
      if (i < s.firstIndex) { s.firstIndex = i; s.firstScene = sceneNumber; }
      s.occurrences.push({ index: i, elementId: el.id, sceneNumber, sceneHeading, lineText: el.text || "" });
      let j = i + 1;
      while (j < els.length && (els[j].type === "dialogue" || els[j].type === "parenthetical")) {
        s.dialogueWords += _wordCount(els[j].text);
        j += 1;
      }
      currentSubjects = new Set([key]); // the speaker is on screen for the action that follows
      continue;
    }

    if (type === "action") {
      const text = el.text || "";
      const words = _wordCount(text);
      if (!words) continue;
      const matched = [];
      for (const m of matchers) { if (m.re.test(text)) matched.push(m.key); }
      const targets = matched.length ? matched : Array.from(currentSubjects);
      for (const key of targets) ensure(key).actionWords += words;
      for (const key of matched) {
        const s = ensure(key);
        if (i < s.firstIndex) { s.firstIndex = i; s.firstScene = sceneNumber; }
        s.occurrences.push({ index: i, elementId: el.id, sceneNumber, sceneHeading, lineText: text });
      }
      if (matched.length) currentSubjects = new Set(matched);
    }
  }

  for (const k of Object.keys(stats)) stats[k].screenTime = stats[k].dialogueWords;

  return { names, stats };
}
